import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeBlob } from "@/lib/api";

export const canRecord = () =>
  typeof window !== "undefined" &&
  "MediaRecorder" in window &&
  !!navigator?.mediaDevices?.getUserMedia;

/**
 * Reusable push-to-talk hook. Reuses the main conversation's recording, transcription
 * and error handling. Provides truthful state and stops mic tracks on cancel/unmount.
 */
export function useVoiceInput({ onTranscript, onError } = {}) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState(null); // "denied" | "unsupported" | "provider"
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      try { recorderRef.current?.stop?.(); } catch (e) { /* ignore */ }
      stopTracks();
    };
  }, [stopTracks]);

  const start = useCallback(async () => {
    setError(null);
    if (!canRecord()) {
      setError("unsupported");
      onError?.("unsupported");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mime = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stopTracks();
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const { text, warning } = await transcribeBlob(blob);
          if (warning === "empty_transcript" || !text) {
            onError?.("empty");
          } else {
            onTranscript?.(text);
          }
        } catch (err) {
          setError("provider");
          onError?.("provider", err);
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      setError("denied");
      onError?.("denied", err);
    }
  }, [onError, onTranscript, stopTracks]);

  const stop = useCallback(() => {
    try { recorderRef.current?.stop?.(); } catch (e) { /* ignore */ }
    setRecording(false);
  }, []);

  const cancel = useCallback(() => {
    try { recorderRef.current?.stop?.(); } catch (e) { /* ignore */ }
    setRecording(false);
    setTranscribing(false);
    stopTracks();
  }, [stopTracks]);

  return { recording, transcribing, error, start, stop, cancel };
}
